// QR Code Generator Utility
// Generates QR codes for visitor badges

/**
 * Generate a QR code data URL for a visitor token
 * @param {string} tokenNo - The visitor's token number
 * @param {string} baseUrl - Base URL for the website (default: https://ssvassgje.in)
 * @returns {Promise<string>} Data URL of the generated QR code image
 */
export const generateVisitorQRCode = async (tokenNo, baseUrl = 'https://demo1.patronservices.in') => {
  try {
    // Dynamic import to avoid bundling issues
    const QRCode = (await import('qrcode')).default;
    
    if (!tokenNo) {
      console.warn('No token number provided for QR code generation');
      return '';
    }

    // Create the URL that the QR code will point to
    const visitorUrl = `${baseUrl}/visitor/${tokenNo}`;

    // Generate QR code as data URL
    const qrCodeDataUrl = await QRCode.toDataURL(visitorUrl, {
      errorCorrectionLevel: 'M', // Medium error correction
      type: 'image/png',
      quality: 0.95,
      margin: 1,
      width: 200, // QR code width in pixels
      color: {
        dark: '#000000', // QR code color
        light: '#FFFFFF' // Background color
      }
    });

    return qrCodeDataUrl;
  } catch (error) {
    console.error('Error generating QR code:', error);
    return '';
  }
};

/**
 * Generate multiple QR codes for bulk printing
 * @param {Array<string>} tokenNumbers - Array of token numbers
 * @param {string} baseUrl - Base URL for the website
 * @returns {Promise<Object>} Map of token numbers to QR code data URLs
 */
export const generateBulkQRCodes = async (tokenNumbers, baseUrl = 'https://demo1.patronservices.in') => {
  const qrCodes = {};
  
  try {
    // Generate all QR codes in parallel
    const promises = tokenNumbers.map(async (tokenNo) => {
      const qrCode = await generateVisitorQRCode(tokenNo, baseUrl);
      return { tokenNo, qrCode };
    });

    const results = await Promise.all(promises);
    
    // Convert array to object map
    results.forEach(({ tokenNo, qrCode }) => {
      qrCodes[tokenNo] = qrCode;
    });

    return qrCodes;
  } catch (error) {
    console.error('Error generating bulk QR codes:', error);
    return qrCodes;
  }
};

const qrCodeUtils = {
  generateVisitorQRCode,
  generateBulkQRCodes
};

export default qrCodeUtils;
